import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDebounce } from '../../hooks';
import type { Instrument } from '../../features/instruments/useInstruments';
import { useReportingCurrencies } from '../../features/reports/useReportingCurrencies';
import { DetailPanelError, DetailPanelLoading } from '../widgets';

interface ReportingCurrencyDialogProps {
  value: string | null;
  onChange: (currencyCode: string) => void;
  onClose: () => void;
}

function currencyCode(currency: Instrument): string {
  return currency.symbol.trim().toUpperCase();
}

export function ReportingCurrencyDialog({ value, onChange, onClose }: ReportingCurrencyDialogProps) {
  const searchRef = useRef<HTMLInputElement>(null);
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebounce(searchText, 200);
  const currenciesQuery = useReportingCurrencies();
  const currencies = currenciesQuery.data ?? [];

  useEffect(() => {
    searchRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const matches = useMemo(() => {
    const search = debouncedSearch.trim().toLowerCase();
    const sorted = [...currencies].sort((a, b) => currencyCode(a).localeCompare(currencyCode(b)));
    if (!search) return sorted;
    return sorted.filter(
      (currency) =>
        currencyCode(currency).toLowerCase().includes(search) ||
        (currency.description ?? '').toLowerCase().includes(search)
    );
  }, [currencies, debouncedSearch]);

  const typedCode = debouncedSearch.trim().toUpperCase();
  const canUseTypedCode =
    typedCode.length > 0 && !matches.some((currency) => currencyCode(currency) === typedCode);

  const choose = (code: string) => {
    onChange(code);
    onClose();
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end bg-black/30 p-3 sm:items-center sm:justify-center"
      onMouseDown={onClose}
    >
      <section
        className="w-full max-w-md overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reporting-currency-dialog-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <h2 id="reporting-currency-dialog-title" className="text-sm font-medium text-gray-800">
            Reporting currency
          </h2>
          <button
            type="button"
            className="rounded px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 hover:text-gray-900"
            onClick={onClose}
          >
            Close
          </button>
        </div>
        <div className="p-3">
          <input
            ref={searchRef}
            type="search"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            placeholder="Search currency code or name"
            className="w-full rounded-md border-2 border-gray-900 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
          />
        </div>
        <div className="max-h-72 overflow-y-auto border-t border-gray-100">
          {currenciesQuery.isLoading && currencies.length === 0 ? (
            <DetailPanelLoading message="Loading currencies..." />
          ) : null}
          {currenciesQuery.error && currencies.length === 0 ? (
            <div className="p-3">
              <DetailPanelError message="Failed to load currencies" />
            </div>
          ) : null}
          {!currenciesQuery.isLoading && !currenciesQuery.error
            ? matches.map((currency) => {
                const code = currencyCode(currency);
                const selected = code === value?.trim().toUpperCase();
                return (
                  <button
                    key={currency.id}
                    type="button"
                    className={`flex w-full items-center gap-2 border-b border-gray-100 px-4 py-2 text-left text-sm hover:bg-primary-50 ${
                      selected ? 'bg-primary-50 font-medium text-gray-900' : 'text-gray-700'
                    }`}
                    onClick={() => choose(code)}
                  >
                    <span className="w-12 shrink-0 font-medium">{code}</span>
                    <span className="min-w-0 truncate text-gray-600">{currency.description}</span>
                  </button>
                );
              })
            : null}
          {!currenciesQuery.isLoading && !currenciesQuery.error && canUseTypedCode ? (
            <button
              type="button"
              className="flex w-full items-center gap-2 border-b border-gray-100 px-4 py-2 text-left text-sm text-gray-700 hover:bg-primary-50"
              onClick={() => choose(typedCode)}
            >
              <span className="font-medium">Use “{typedCode}”</span>
              <span className="text-xs text-gray-500">Not in currency list</span>
            </button>
          ) : null}
          {!currenciesQuery.isLoading && !currenciesQuery.error && matches.length === 0 && !canUseTypedCode ? (
            <p className="px-4 py-3 text-xs text-gray-500">No currencies matched.</p>
          ) : null}
        </div>
      </section>
    </div>,
    document.body
  );
}
